import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsTaskCatalogOptions, getOrgAgentsToolsOptions } from '@onefootprint/fetch-dashboard';
import { IcoBolt16, IcoBroadcast16, IcoBuilding16, IcoCheckCircle16, IcoChecklist16, IcoChevronLeftBig16, IcoClipboard16, IcoConnections16, IcoFileText16, IcoMegaphone16, type Icon, IcoPencil16, IcoPinMarker16, IcoSearch16, IcoShield16, IcoShuffle16, IcoStar16, IcoStore16, IcoTag16, IcoUserCircle16, IcoUsers16, IcoVoter16, IcoWarning16, IcoWorld16, IcoWww16 } from '@onefootprint/icons';
import type { AgentTool, TaskCatalogEntry, TaskCatalogKey, TaskCategory, TaskSubject } from '@onefootprint/request-types/dashboard';
import { Dialog, LoadingSpinner, SearchInput, Tabs } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useEffect, useRef } from 'react';
import ErrorComponent from '@/components/error';
import CatalogConfirmation from './catalog-confirmation';
import { type AgentState, hasRequirements, missingRequirements } from './catalog-requirements';
import { type PickerDispatch, type PickerState, usePickerState } from './picker-state';
type CatalogPick = {
  key: TaskCatalogKey;
  addInputs: string[];
  addTools: AgentTool[];
};
type TaskTemplatePickerProps = {
  open: boolean;
  onClose: () => void;
  onPickTask: () => void;
  onPaste?: () => void;
  onPickRules: () => void;
  onPickDecision: () => void;
  /** Decision nodes sit behind the `agent_decision_node` feature gate. */
  showDecision: boolean;
  onPickCatalog: (pick: CatalogPick) => void;
  inputs: AgentState['inputs'];
  producedKeys: Set<string>;
  tools: AgentTool[];
  inputsLocked?: boolean;
  toolsLocked?: boolean;
  disabled?: boolean;
};

// Typed loosely for lookups: the dashboard can meet a key newer than its generated types.
const ENTRY_ICONS: Partial<Record<string, Icon>> = {
  research_online: IcoSearch16,
  enrich_company_profile: IcoBuilding16,
  check_business_registration: IcoFileText16,
  find_business_on_google_maps: IcoPinMarker16,
  find_business_reviews: IcoStar16,
  review_business_web_presence: IcoWww16,
  review_merchant_website: IcoStore16,
  review_business_adverse_media: IcoBroadcast16,
  classify_business_activity: IcoTag16,
  research_geographic_exposure: IcoWorld16,
  person_pep_review: IcoVoter16,
  review_person_adverse_media: IcoMegaphone16,
  verify_business_profile: IcoCheckCircle16,
  review_business_registration_document: IcoFileText16,
  review_policy_and_controls: IcoChecklist16,
  verify_beneficial_ownership: IcoUsers16,
  map_trust_ownership_structure: IcoConnections16,
  compare_business_watchlist_hits: IcoShield16,
  compare_person_watchlist_hits: IcoUserCircle16,
  check_prohibited_activity: IcoWarning16
} satisfies Record<TaskCatalogKey, Icon>;
const STAGES: {
  category: TaskCategory;
  title: string;
}[] = [{
  category: 'research',
  title: 'Research'
}, {
  category: 'verify',
  title: 'Verify'
}, {
  category: 'policy',
  title: 'Apply policy'
}, {
  category: 'assess',
  title: 'Assess'
}];
const SUBJECTS: {
  subject: TaskSubject;
  title: string;
}[] = [{
  subject: 'business',
  title: 'Business'
}, {
  subject: 'person',
  title: 'Person'
}];
type Choice = {
  id: string;
  name: string;
  description: string;
  icon: Icon;
  onSelect: () => void;
};
type ChoiceListProps = {
  choices: Choice[];
  disabled?: boolean;
};
const ChoiceList = t0 => {
  const $ = _c(7);
  const {
    choices,
    disabled
  } = t0;
  let t1;
  if ($[0] !== choices || $[1] !== disabled) {
    let t2;
    if ($[3] !== disabled) {
      t2 = choice => {
        const ChoiceIcon = choice.icon;
        return <li key={choice.id}><button type="button" data-choice-id={choice.id} disabled={disabled} onClick={choice.onSelect} className={cx("flex w-full items-start gap-2.5 rounded-default border border-tertiary p-3 text-left outline-none transition-colors focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset", disabled ? "cursor-default" : "cursor-pointer hover:bg-hover")}><span aria-hidden={true} className={cx("mt-0.5 flex size-5 shrink-0 items-center justify-center", disabled ? "text-quaternary" : "text-primary")}><ChoiceIcon /></span><span className="min-w-0 flex-1"><span className={cx("block text-label-3", disabled ? "text-tertiary" : "text-primary")}>{choice.name}</span><span className="mt-0.5 block text-body-3 text-tertiary">{choice.description}</span></span></button></li>;
      };
      $[3] = disabled;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = choices.map(t2);
    $[0] = choices;
    $[1] = disabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <ul className="flex flex-col gap-2">{t1}</ul>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const PickerBody = t0 => {
  const $ = _c(79);
  const {
    onClose,
    onPickTask,
    onPaste,
    onPickRules,
    onPickDecision,
    showDecision,
    onPickCatalog,
    inputs,
    producedKeys,
    tools,
    inputsLocked,
    toolsLocked,
    disabled,
    state,
    dispatch
  } = t0;
  const {
    group,
    query,
    pending,
    returnFocusTo
  } = state;
  const listRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== dispatch || $[1] !== returnFocusTo) {
    t1 = () => {
      if (!returnFocusTo) {
        return;
      }
      listRef.current?.querySelector(`[data-choice-id="${returnFocusTo}"]`)?.focus();
      dispatch({
        type: "focusReturned"
      });
    };
    t2 = [returnFocusTo, dispatch];
    $[0] = dispatch;
    $[1] = returnFocusTo;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getOrgAgentsTaskCatalogOptions();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const catalogQuery = useQuery(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getOrgAgentsToolsOptions();
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const toolsQuery = useQuery(t4);
  let t10;
  let t11;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] !== catalogQuery || $[7] !== disabled || $[8] !== dispatch || $[9] !== group || $[10] !== inputs || $[11] !== inputsLocked || $[12] !== onClose || $[13] !== onPaste || $[14] !== onPickCatalog || $[15] !== onPickDecision || $[16] !== onPickRules || $[17] !== onPickTask || $[18] !== pending || $[19] !== producedKeys || $[20] !== query || $[21] !== showDecision || $[22] !== tools || $[23] !== toolsLocked || $[24] !== toolsQuery.data?.tools || $[25] !== toolsQuery.error || $[26] !== toolsQuery.isPending) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const toolCatalog = toolsQuery.data?.tools ?? [];
      const agent = {
        inputs,
        producedKeys,
        tools
      };
      let t12;
      if ($[34] !== onClose) {
        t12 = onPick => () => {
          onPick();
          onClose();
        };
        $[34] = onClose;
        $[35] = t12;
      } else {
        t12 = $[35];
      }
      const pick = t12;
      const pickCatalog = entry => {
        if (hasRequirements(missingRequirements(entry, agent, toolCatalog))) {
          dispatch({
            type: "confirm",
            entry
          });
          return;
        }
        onPickCatalog({
          key: entry.key,
          addInputs: [],
          addTools: []
        });
        onClose();
      };
      if (pending) {
        let t13;
        if ($[36] !== onClose || $[37] !== onPickCatalog || $[38] !== pending.key) {
          t13 = additions => {
            onPickCatalog({
              key: pending.key,
              ...additions
            });
            onClose();
          };
          $[36] = onClose;
          $[37] = onPickCatalog;
          $[38] = pending.key;
          $[39] = t13;
        } else {
          t13 = $[39];
        }
        t11 = <CatalogConfirmation entry={pending} requirements={missingRequirements(pending, agent, toolCatalog)} inputsLocked={inputsLocked} toolsLocked={toolsLocked} disabled={disabled} onConfirm={t13} />;
        break bb0;
      }
      let t13;
      if ($[40] !== onPickTask || $[41] !== pick) {
        t13 = {
          id: "blank",
          name: "Blank task",
          description: "Write instructions and define the outputs for a custom task.",
          icon: IcoPencil16,
          onSelect: pick(onPickTask)
        };
        $[40] = onPickTask;
        $[41] = pick;
        $[42] = t13;
      } else {
        t13 = $[42];
      }
      let t14;
      if ($[43] !== onPickRules || $[44] !== pick) {
        t14 = {
          id: "rules",
          name: "Rules",
          description: "Use conditions to make a decision from earlier task outputs.",
          icon: IcoShuffle16,
          onSelect: pick(onPickRules)
        };
        $[43] = onPickRules;
        $[44] = pick;
        $[45] = t14;
      } else {
        t14 = $[45];
      }
      let own;
      if ($[46] !== onPaste || $[47] !== onPickDecision || $[48] !== pick || $[49] !== showDecision || $[50] !== t13 || $[51] !== t14) {
        own = [t13, t14];
        if (showDecision) {
          let t15;
          if ($[53] !== onPickDecision || $[54] !== pick) {
            t15 = {
              id: "decision",
              name: "Decision",
              description: "Answer typed yes/no, pick-one or score questions about the case in under a second.",
              icon: IcoBolt16,
              onSelect: pick(onPickDecision)
            };
            $[53] = onPickDecision;
            $[54] = pick;
            $[55] = t15;
          } else {
            t15 = $[55];
          }
          own.push(t15);
        }
        if (onPaste) {
          let t15;
          if ($[56] !== onPaste || $[57] !== pick) {
            t15 = {
              id: "paste",
              name: "Paste task",
              description: "Add a task copied from another agent.",
              icon: IcoClipboard16,
              onSelect: pick(onPaste)
            };
            $[56] = onPaste;
            $[57] = pick;
            $[58] = t15;
          } else {
            t15 = $[58];
          }
          own.push(t15);
        }
        $[46] = onPaste;
        $[47] = onPickDecision;
        $[48] = pick;
        $[49] = showDecision;
        $[50] = t13;
        $[51] = t14;
        $[52] = own;
      } else {
        own = $[52];
      }
      const renderTemplates = subject => {
        if (catalogQuery.isPending || toolsQuery.isPending) {
          return <LoadingSpinner />;
        }
        const error = catalogQuery.error ?? toolsQuery.error;
        if (error) {
          return <ErrorComponent error={error} />;
        }
        const needle = query.trim().toLowerCase();
        const entries = (catalogQuery.data?.entries ?? []).filter(entry_0 => entry_0.subjects.includes(subject) && `${entry_0.name} ${entry_0.description}`.toLowerCase().includes(needle));
        if (entries.length === 0 && needle) {
          return <p className="text-body-3 text-tertiary">No templates match "{query.trim()}".</p>;
        }
        return STAGES.map(t15 => {
          const {
            category,
            title
          } = t15;
          const choices = entries.filter(entry_1 => entry_1.category === category).map(entry_2 => ({
            id: entry_2.key,
            name: entry_2.name,
            description: entry_2.description,
            icon: ENTRY_ICONS[entry_2.key] ?? IcoFileText16,
            onSelect: () => pickCatalog(entry_2)
          }));
          if (!choices.length) {
            return null;
          }
          return <section key={category} aria-label={title} className="flex flex-col gap-2"><h3 className="text-label-3 text-secondary">{title}</h3><ChoiceList choices={choices} disabled={disabled} /></section>;
        });
      };
      t8 = "flex flex-col gap-4 px-6 pt-6";
      let t16;
      if ($[59] !== dispatch) {
        t16 = value => dispatch({
          type: "showGroup",
          group: value
        });
        $[59] = dispatch;
        $[60] = t16;
      } else {
        t16 = $[60];
      }
      let t17;
      if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = [{
          label: "From scratch",
          value: "own"
        }, ...SUBJECTS.map(_temp)];
        $[61] = t17;
      } else {
        t17 = $[61];
      }
      if ($[62] !== group || $[63] !== t16 || $[64] !== t17) {
        t9 = <Tabs value={group} onChange={t16} options={t17} />;
        $[62] = group;
        $[63] = t16;
        $[64] = t17;
        $[65] = t9;
      } else {
        t9 = $[65];
      }
      if ($[66] !== dispatch || $[67] !== group || $[68] !== query) {
        t10 = group !== "own" && <SearchInput size="compact" aria-label="Search templates" placeholder="Search templates" value={query} onChangeText={text => dispatch({
          type: "search",
          query: text
        })} onReset={() => dispatch({
          type: "search",
          query: ""
        })} />;
        $[66] = dispatch;
        $[67] = group;
        $[68] = query;
        $[69] = t10;
      } else {
        t10 = $[69];
      }
      t5 = listRef;
      t6 = "-mx-6 flex max-h-[calc(100vh-16rem)] flex-col gap-5 overflow-y-auto px-6 pb-6";
      t7 = group === "own" ? <ChoiceList choices={own} disabled={disabled} /> : renderTemplates(group);
    }
    $[6] = catalogQuery;
    $[7] = disabled;
    $[8] = dispatch;
    $[9] = group;
    $[10] = inputs;
    $[11] = inputsLocked;
    $[12] = onClose;
    $[13] = onPaste;
    $[14] = onPickCatalog;
    $[15] = onPickDecision;
    $[16] = onPickRules;
    $[17] = onPickTask;
    $[18] = pending;
    $[19] = producedKeys;
    $[20] = query;
    $[21] = showDecision;
    $[22] = tools;
    $[23] = toolsLocked;
    $[24] = toolsQuery.data?.tools;
    $[25] = toolsQuery.error;
    $[26] = toolsQuery.isPending;
    $[27] = t10;
    $[28] = t11;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t10 = $[27];
    t11 = $[28];
    t5 = $[29];
    t6 = $[30];
    t7 = $[31];
    t8 = $[32];
    t9 = $[33];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[70] !== t5 || $[71] !== t6 || $[72] !== t7) {
    t12 = <div ref={t5} className={t6}>{t7}</div>;
    $[70] = t5;
    $[71] = t6;
    $[72] = t7;
    $[73] = t12;
  } else {
    t12 = $[73];
  }
  let t13;
  if ($[74] !== t10 || $[75] !== t12 || $[76] !== t8 || $[77] !== t9) {
    t13 = <div className={t8}>{t9}{t10}{t12}</div>;
    $[74] = t10;
    $[75] = t12;
    $[76] = t8;
    $[77] = t9;
    $[78] = t13;
  } else {
    t13 = $[78];
  }
  return t13;
};
const TaskTemplatePicker = t0 => {
  const $ = _c(22);
  let onClose;
  let open;
  let props;
  if ($[0] !== t0) {
    ({
      open,
      onClose,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onClose;
    $[2] = open;
    $[3] = props;
  } else {
    onClose = $[1];
    open = $[2];
    props = $[3];
  }
  const [state, dispatch] = usePickerState();
  let t1;
  if ($[4] !== dispatch || $[5] !== onClose) {
    t1 = () => {
      dispatch({
        type: "reset"
      });
      onClose();
    };
    $[4] = dispatch;
    $[5] = onClose;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleClose = t1;
  const isConfirming = state.pending !== null;
  const t2 = isConfirming ? "compact" : "large";
  let t3;
  if ($[7] !== dispatch || $[8] !== isConfirming) {
    t3 = isConfirming ? {
      ariaLabel: "Back",
      component: IcoChevronLeftBig16,
      onClick: () => dispatch({
        type: "back"
      })
    } : undefined;
    $[7] = dispatch;
    $[8] = isConfirming;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== dispatch || $[11] !== handleClose || $[12] !== open || $[13] !== props || $[14] !== state) {
    t4 = open && <PickerBody {...props} onClose={handleClose} state={state} dispatch={dispatch} />;
    $[10] = dispatch;
    $[11] = handleClose;
    $[12] = open;
    $[13] = props;
    $[14] = state;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== handleClose || $[17] !== open || $[18] !== t2 || $[19] !== t3 || $[20] !== t4) {
    t5 = <Dialog noPadding={true} open={open} title="Add a task" size={t2} headerIcon={t3} onClose={handleClose}>{t4}</Dialog>;
    $[16] = handleClose;
    $[17] = open;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
export type { CatalogPick, TaskTemplatePickerProps };
export { PickerBody };
export default TaskTemplatePicker;
function _temp(t0) {
  const {
    subject: subject_0,
    title: title_0
  } = t0;
  return {
    label: title_0,
    value: subject_0
  };
}